export default defineNuxtPlugin(() => {
  const originalFetch = globalThis.fetch
  let isRedirecting = false
  const apiPathRegex = /\/(?:api|public-api)(?:\/|$)/
  const correlationHeader = 'x-correlation-id'

  const generateCorrelationId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`
  }

  const getRequestUrl = (input: RequestInfo | URL): string => {
    if (input instanceof Request) {
      return input.url
    }

    if (input instanceof URL) {
      return input.toString()
    }

    return String(input)
  }

  const shouldAttachCorrelationId = (input: RequestInfo | URL): boolean => {
    const requestUrl = getRequestUrl(input)

    if (requestUrl.startsWith('/')) {
      return apiPathRegex.test(requestUrl)
    }

    if (requestUrl.startsWith('http://') || requestUrl.startsWith('https://')) {
      try {
        const parsed = new URL(requestUrl)
        return apiPathRegex.test(parsed.pathname)
      } catch {
        return false
      }
    }

    return false
  }

  const withCorrelationIdHeader = (
    input: RequestInfo | URL,
    init?: RequestInit
  ): [RequestInfo | URL, RequestInit | undefined] => {
    if (!shouldAttachCorrelationId(input)) {
      return [input, init]
    }

    const correlationId = generateCorrelationId()

    if (input instanceof Request) {
      const headers = new Headers(input.headers)
      if (!headers.has(correlationHeader)) {
        headers.set(correlationHeader, correlationId)
      }

      return [new Request(input, { ...init, headers }), undefined]
    }

    const headers = new Headers(init?.headers)
    if (!headers.has(correlationHeader)) {
      headers.set(correlationHeader, correlationId)
    }

    return [input, { ...init, headers }]
  }

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const [nextInput, nextInit] = withCorrelationIdHeader(input, init)
    const response = await originalFetch(nextInput, nextInit)

    // Handle 401 Unauthorized - redirect to login
    if (response.status === 401 && !isRedirecting) {
      const currentPath = window.location.pathname

      if (!currentPath.startsWith('/login')) {
        isRedirecting = true
        const fullPath = window.location.pathname + window.location.search

        // Use setTimeout to ensure the flag is set before any subsequent requests
        setTimeout(() => {
          navigateTo({
            path: '/login',
            query: { redirect: fullPath }
          })
        }, 0)
      }
    }

    // Handle 403 Forbidden - redirect to unauthorized page
    if (response.status === 403 && !isRedirecting) {
      const currentPath = window.location.pathname

      if (!currentPath.startsWith('/unauthorized')) {
        isRedirecting = true

        // Use setTimeout to ensure the flag is set before any subsequent requests
        setTimeout(() => {
          navigateTo('/unauthorized')
        }, 0)
      }
    }

    return response
  }
})
